// src/web/client/src/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Store, Sparkles } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

export default function Login() {
  const [shopDomain, setShopDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Check if we're in an iframe
    if (window.top !== window.self) {
      console.log('Running inside iframe - OAuth must be done in parent window');
    }
  }, []);

  // Demo login
  const handleDemoLogin = () => {
    setLoading(true);
    
    localStorage.setItem('shop_domain', 'demo-shop.myshopify.com');
    localStorage.setItem('shopId', 'demo');
    localStorage.setItem('isDemoMode', 'true');
    
    toast({
      title: "Welcome to Demo Mode!",
      description: "Exploring Ecomoria with sample data...",
    });
    
    setTimeout(() => {
      navigate('/dashboard');
    }, 1000);
  };

  // Shopify OAuth - FIXED to handle iframe issues
  const handleShopifyConnect = () => {
    if (!shopDomain || shopDomain.trim() === '') {
      toast({
        title: "Enter your shop domain",
        description: "Please enter your Shopify store domain",
        variant: "destructive"
      });
      return;
    }
    
    // Normalize domain
    let domain = shopDomain.trim().toLowerCase();
    if (!domain.includes('.myshopify.com')) {
      domain = domain.replace(/\s+/g, '-'); // Replace spaces with dashes
      domain = domain.replace(/[^a-z0-9-]/g, ''); // Remove special characters
      domain = `${domain}.myshopify.com`;
    }
    
    setLoading(true);
    
    // Save domain before redirect
    localStorage.setItem('shop_domain', domain);
    localStorage.setItem('shopDomain', domain);
    
    // Check if we're embedded
    const isEmbedded = window.top !== window.self;
    
    if (isEmbedded) {
      // Use the OAuth redirect handler for embedded apps
      const redirectUrl = `/oauth-redirect.html?shop=${domain}&embedded=true`;
      window.location.href = redirectUrl;
    } else {
      // Direct OAuth redirect for standalone
      const authUrl = `https://api.ecomoria.com/auth?shop=${domain}`;
      window.location.href = authUrl;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center">
              <Store className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">Welcome to Ecomoria</CardTitle>
          {window.top !== window.self && (
            <p className="text-sm text-gray-500 mt-2">
              Running in Shopify Admin
            </p>
          )}
        </CardHeader>
        
        <CardContent className="space-y-6">
                    {/* Demo Access - HIDDEN for Shopify Review */}
          {/* Quick Demo Access }
          <div className="space-y-2">
            <Button 
              onClick={handleDemoLogin} 
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              disabled={loading}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {loading ? "Loading..." : "Explore Demo Store"}
            </Button>
            <p className="text-xs text-center text-gray-500">
              Try Ecomoria with sample data - no setup required
            </p>
          </div>
                    {/* Demo Access - HIDDEN for Shopify Review */}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500"> connect your store</span>
            </div>
          </div>

          {/* Shopify Connect 
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="shop">Your Shopify Store</Label>
              <Input
                id="shop"
                type="text"
                placeholder="your-store-name"
                value={shopDomain}
                onChange={(e) => setShopDomain(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleShopifyConnect()}
                disabled={loading}
              />
              <p className="text-xs text-gray-500">
                Enter your store name (without .myshopify.com)
              </p>
            </div>
            
            <Button
              onClick={handleShopifyConnect}
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={loading || !shopDomain}
            >
              <Store className="w-4 h-4 mr-2" />
              Connect with Shopify
            </Button>
          </div>
*/}
          {/* Help Section */}
          <div className="text-center text-sm text-gray-500">
            <p>New to Ecomoria?</p>
            <a
              // The merchant-facing Shopify App Store listing for Ecomoria.
              // The previous value (partners.shopify.com) was the Shopify
              // Partner *Dashboard* — useful only to people building apps,
              // not to a merchant who wants to install ours. A merchant
              // landing there saw a login screen for an account they don't
              // have and bounced.
              href="https://apps.shopify.com/ecomoria"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:underline"
            >
              Install from Shopify App Store
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}